'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@av/ui/popover';
import { Button, ButtonProps } from '@av/ui/button';
import { TAX_HIGH_FACTOR, cn, useDebounce } from '@av/utils';
import { Input, Slider } from '@av/ui/form';
import { ChevronDown } from 'lucide-react';
import { RangeBoundaries } from 'instantsearch.js/es/connectors/range/connectRange';
import { useTaxState } from '@/app/components/context/tax-state-context';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';

const ProductPriceRange = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { range?: { min: number; max: number } }
>(({ className, range, ...props }, ref) => {
  const min = Math.floor((range?.min || 0) / 100);
  const max = Math.ceil((range?.max || 0) / 100);
  const { isTaxIncluded } = useTaxState();

  const [minAfterTax, maxAfterTax] = useMemo(
    () => [
      min * (isTaxIncluded ? 1 + TAX_HIGH_FACTOR : 1),
      max * (isTaxIncluded ? 1 + TAX_HIGH_FACTOR : 1),
    ],
    [isTaxIncluded, min, max]
  );

  const [value, setValue] = useState<(number | undefined)[] | undefined>(undefined);

  const debouncedValue = useDebounce(value, 200);

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const formatRefinedValue = (n = 0) => {
    return Math.floor(n / (isTaxIncluded ? 1 + TAX_HIGH_FACTOR : 1));
  };

  const getRefinedValue = useCallback(
    (v: [number, number]) => {
      return [
        formatRefinedValue(Math.max(minAfterTax, v[0]!)),
        formatRefinedValue(Math.min(maxAfterTax, v[1]!)),
      ] as RangeBoundaries;
    },
    [minAfterTax, maxAfterTax]
  );

  useEffect(() => {
    setValue([
      searchParams.get('min') ? +searchParams.get('min')! : minAfterTax,
      searchParams.get('max') ? +searchParams.get('max')! : maxAfterTax,
    ]);
  }, []);

  useEffect(() => {
    if (!debouncedValue) return;
    const debouncedValuesExist = debouncedValue[0] !== undefined && debouncedValue[1];

    if (debouncedValuesExist && debouncedValue[1] !== minAfterTax) {
      const [refinedMin, refinedMax] = getRefinedValue(debouncedValue as [number, number]);
      const params = new URLSearchParams(searchParams);
      const oldMin = params.get('min');
      const oldMax = params.get('max');
      const page = params.get('pagina');

      if (refinedMin && refinedMin !== minAfterTax) {
        params.set('min', refinedMin.toString());
      } else {
        params.delete('min');
      }

      if (refinedMax && refinedMax !== maxAfterTax) {
        params.set('max', refinedMax.toString());
      } else {
        params.delete('max');
      }

      const newMin = params.get('min');
      const newMax = params.get('max');

      if ((oldMin !== newMin || oldMax !== newMax) && page && +page > 1) {
        params.delete('pagina');
      }

      router.push(`${pathname}?${params.toString()}`, undefined);
    }
  }, [debouncedValue, minAfterTax, maxAfterTax]);

  const formatStringInput = (v: string) => {
    return v
      ? Math.ceil(Math.min(Math.max(+v.replace(/[^0-9]/g, ''), minAfterTax), maxAfterTax))
      : undefined;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          {...props}
          className={cn('h-9 rounded-full bg-white', className)}
          ref={ref}
        >
          Prijs
          <ChevronDown className="ml-1 size-4" />
        </Button>
      </PopoverTrigger>
      {!!value && (
        <PopoverContent align="center" className="z-10 flex items-center space-x-2 bg-white p-2">
          <Input
            className="h-9 w-16 border-gray-400 pl-3 pr-1 text-sm"
            type="number"
            onChange={(e) => setValue([formatStringInput(e.target.value), value[1]])}
            step={1}
            value={Math.floor(value[0] || 0)}
          />
          <Slider
            defaultValue={value as number[]}
            value={value as number[]}
            max={Math.ceil(maxAfterTax)}
            min={Math.floor(minAfterTax)}
            onValueChange={setValue}
            step={1}
            className={cn('w-40', className)}
          />
          <Input
            className="h-9 w-16 border-gray-400 pl-3 pr-1 text-sm"
            type="number"
            onChange={(e) => setValue([value[0], formatStringInput(e.target.value)])}
            step={1}
            value={Math.ceil(value[1] || 0)}
          />
        </PopoverContent>
      )}
    </Popover>
  );
});
ProductPriceRange.displayName = 'ProductPriceRange';

export { ProductPriceRange };
